import { z } from "zod";

import { effectLocationTokenSchema } from "~/shared/types/EffectLocation";

export const REGULATORY_FLAG_MESSAGE_MAX_LENGTH = 500;

/**
 * Regulatory effects recognized by the Rules Engine for rules in the
 * `ruleDomainConstants.regulatory` domain.
 *
 * - `Block`: prevents the Order from proceeding
 * - `Flag`: surfaces the failed condition without preventing progress
 * - `Approval`: reports a requirement whose coverage is resolved by Orders, never by this evaluator
 *
 * Notify is not an effect type: it is an add-on carried by a Block or Flag effect's config (`notify`). Approval cannot carry it.
 * A stored `notify` effect type is a retired shape and is read as an unrecognized type, so it fails closed.
 *
 * Values are stored in `RuleEffect.type`, which is a free `String` column.
 */
export enum RegulatoryEffectType {
  Block = "block",
  Flag = "flag",
  Approval = "approval",
}

export const regulatoryEffectTypeSchema = z.nativeEnum(RegulatoryEffectType);

/**
 * The payload a regulatory effect carries in `RuleEffect.config`.
 *
 * `location` is where the effect resolves: the effect fires at that one point and nowhere else. It is
 * required, because an effect that cannot say where it applies cannot be routed at all, and a rule that
 * cannot be routed must not read as a rule that matched nothing — `resolveRegulatoryEffects` throws on it.
 *
 * Stored in `RuleEffect.config`, which is already `Json` (`prisma/schema.prisma:6681`), so carrying a
 * location needs no migration. It is also why this is parsed rather than asserted: the column has no shape
 * constraint, so a missing or malformed location is reachable from data.
 *
 * `.passthrough()` so a key a later effect adds to the config is not a parse failure here. Widening the
 * required half is the deliberate edit; tolerating the unrequired half is not.
 *
 * **One Rule owns its own `RuleEffect` row — required, and unenforced.** `Rule.effectId` has no unique constraint and
 * the reverse relation is `rules Rule[]` (`prisma/schema.prisma`), so two Rules may point at one Effect. Enforcing it
 * later requires a `@unique`/`@@unique([effectId])` schema decision and a migration that decides how to handle any
 * shared effect rows.
 *
 * `RuleEffect` has no `orgId` and is listed in `IGNORE_ORG_MODELS` (`src/server/infra/orm/prisma.middleware.ts:34`).
 * `updateRuleEffect` writes by effect id alone, so a shared Effect lets one org re-scope another org's compliance
 * gate. Writers must reach an Effect through its org-scoped Rule, create a fresh Effect for every Rule, and never
 * accept an arbitrary Effect id.
 */
export const regulatoryEffectConfigSchema = z
  .object({
    location: effectLocationTokenSchema,
    // Effect rows predate this optional Flag-only authoring field. Readers parse it at the
    // regulatory boundary, while writers only include it for Flag effects.
    message: z.string().trim().min(1).max(REGULATORY_FLAG_MESSAGE_MAX_LENGTH).optional(),
    // Notify add-on (Block or Flag). Recipients live on the NotificationRoute keyed by this Rule's id.
    notify: z.boolean().optional(),
  })
  .passthrough();

export type RegulatoryEffectConfig = z.infer<typeof regulatoryEffectConfigSchema>;

/** Approval setup is deliberately limited to these three existing Sales Order gates. */
export const isApprovalEffectLocation = (location: string): boolean =>
  location === "sales_order.status_transition.draft_to_confirmed" ||
  location === "sales_order.status_transition.confirmed_to_shipped" ||
  location === "sales_order.status_transition.shipped_to_delivered";

/** Approval authority lives in RuleApprover, never in this JSON or a Notify route. */
export const approvalEffectConfigSchema = z
  .object({
    location: effectLocationTokenSchema.refine(isApprovalEffectLocation, "Approval is available at Sales Order status-change steps only."),
    message: z.string().trim().min(1).max(REGULATORY_FLAG_MESSAGE_MAX_LENGTH).optional(),
  })
  .strict();

export const parseRegulatoryEffectConfig = (type: RegulatoryEffectType, config: unknown) =>
  type === RegulatoryEffectType.Approval ? approvalEffectConfigSchema.safeParse(config) : regulatoryEffectConfigSchema.safeParse(config);
